/**
 * Tenant tracking pixels — loads each company's FB Pixel + Google Ads tag
 * dynamically on their public quote funnel.
 *
 * Why dynamic injection (instead of in index.html):
 *   • Pixel IDs are per-tenant. Loading them on the platform funnel or other
 *     companies' funnels would pollute their conversion data.
 *   • Companies may add/remove pixels at any time from Settings → Tracking.
 *
 * Security:
 *   • Pixel IDs are validated server-side (TrackingPixels Pydantic model) —
 *     strict regex for FB (8-20 digits), Google Ads (AW-XXXXXXX), and the
 *     conversion label (alphanumeric + _ -). We NEVER accept raw HTML/JS.
 *   • This module only injects the official SDK URLs from facebook.net /
 *     googletagmanager.com with those validated IDs as parameters.
 *
 * Privacy / dedupe:
 *   • Each pixel exposes its API on `window.fbq` (Meta) and `window.gtag`
 *     (Google). Our platform-side pixels in /public/index.html use the SAME
 *     globals — which means we'd double-init if we weren't careful. The
 *     Meta snippet checks `if (f.fbq) return;` so it's safe to call again.
 *     For Google Ads we use a separate `config` call (gtag supports multiple
 *     IDs natively) so it stacks alongside the platform GA4 cleanly.
 */

const LOADED = {
  fb_pixel: new Set(),       // FB Pixel IDs that have been init()'d in this session
  gads: new Set(),            // Google Ads conversion IDs configured
  fb_script_inserted: false,  // Has the fbevents.js <script> been added to <head>?
  gads_script_inserted: false,// Has the gtag.js <script> been added?
};

// ----- Facebook Pixel ------------------------------------------------------
function installFbPixel(pixelId) {
  if (!pixelId || LOADED.fb_pixel.has(pixelId)) return;

  // Base snippet — copy of Meta's standard install code, but factored so we
  // can call it multiple times for multiple pixels without re-injecting the
  // SDK <script>.
  if (!window.fbq) {
    /* eslint-disable */
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    LOADED.fb_script_inserted = true;
  }
  window.fbq("init", String(pixelId));
  window.fbq("track", "PageView");
  LOADED.fb_pixel.add(pixelId);

  // <noscript> fallback img — Meta's standard install includes this so the
  // pixel still fires for users on browsers with JS disabled OR iOS Safari
  // Tracking Prevention that blocks fbevents.js but not img requests.
  // Idempotent — only injects once per pixel ID.
  if (typeof document !== "undefined" && !document.getElementById(`_fbpx_ns_${pixelId}`)) {
    const img = document.createElement("img");
    img.id = `_fbpx_ns_${pixelId}`;
    img.height = 1;
    img.width = 1;
    img.style.display = "none";
    img.alt = "";
    img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1`;
    document.body.appendChild(img);
  }
}

// ----- Manually fire a fresh PageView (used on route-change / step change) -
// Meta's SDK doesn't auto-fire PageView on SPA client-side navigations. Call
// this on important funnel milestones (submitted, thank-you screen) so
// Pixel Helper / Events Manager shows continuous activity.
export function fireTenantPageView(tracking) {
  if (!tracking || typeof window === "undefined") return;
  try {
    if (tracking.fb_pixel_id && typeof window.fbq === "function") {
      window.fbq("trackSingle", String(tracking.fb_pixel_id), "PageView");
    }
  } catch { /* never break the user's flow */ }
}

// ----- Google Ads (gtag.js) ------------------------------------------------
function installGoogleAds(conversionId) {
  if (!conversionId || LOADED.gads.has(conversionId)) return;

  if (!window.gtag) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(conversionId)}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    LOADED.gads_script_inserted = true;
  }
  window.gtag("config", String(conversionId));
  LOADED.gads.add(conversionId);
}

// ----- Public API ----------------------------------------------------------
/**
 * Install whichever tenant pixels are configured on the company doc.
 * Idempotent — safe to call on every render.
 */
export function installTenantPixels(tracking) {
  if (!tracking || typeof window === "undefined") return;
  const fb = (tracking.fb_pixel_id || "").trim();
  const gads = (tracking.google_ads_conversion_id || "").trim();
  if (fb) installFbPixel(fb);
  if (gads) installGoogleAds(gads);
}

/**
 * Fire a "lead submitted" conversion on the tenant's pixels.
 * @param {object} tracking — { fb_pixel_id, google_ads_conversion_id, google_ads_conversion_label }
 * @param {object} payload — { value: number (USD), currency?: string }
 */
export function fireTenantLead(tracking, payload = {}) {
  if (!tracking || typeof window === "undefined") return;
  const value = typeof payload.value === "number" ? payload.value : 0;
  const currency = payload.currency || "USD";

  // Facebook
  try {
    if (tracking.fb_pixel_id && typeof window.fbq === "function") {
      window.fbq("trackSingle", String(tracking.fb_pixel_id), "Lead", { value, currency });
    }
  } catch { /* never break the user's flow */ }

  // Google Ads — conversion needs the "send_to" format `AW-XXX/LABEL`.
  try {
    const cid = (tracking.google_ads_conversion_id || "").trim();
    const label = (tracking.google_ads_conversion_label || "").trim();
    if (cid && label && typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
        send_to: `${cid}/${label}`,
        value,
        currency,
      });
    }
  } catch { /* swallow */ }
}

/**
 * Fire a synthetic test conversion. Used by Settings → Tracking "Send test
 * event" button. Mirrors `fireTenantLead` but with a fixed sentinel value so
 * the owner can spot the event in Meta Events Manager / Google Tag Assistant.
 */
export function fireTenantTestEvent(tracking) {
  // Install if not already (handles "I just pasted my pixel ID and clicked test")
  installTenantPixels(tracking);
  // Defer slightly so the SDK has a moment to bootstrap.
  setTimeout(() => {
    fireTenantLead(tracking, { value: 99.99, currency: "USD" });
  }, 600);
}
